import * as React from 'react'
import { cn } from '@/lib/utils'

type Tone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'

const TONE_RING: Record<Tone, string> = {
  default: 'ring-neutral-200 dark:ring-neutral-200 dark:ring-white/10',
  orange: 'ring-orange-500/20 dark:ring-orange-500/25',
  green: 'ring-emerald-500/20 dark:ring-emerald-500/25',
  blue: 'ring-blue-500/20 dark:ring-blue-500/25',
  red: 'ring-rose-500/20 dark:ring-rose-500/25',
  amber: 'ring-amber-500/20 dark:ring-amber-500/25',
  violet: 'ring-violet-500/20 dark:ring-violet-500/25',
  cyan: 'ring-cyan-500/20 dark:ring-cyan-500/25',
}

interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: Tone
  interactive?: boolean
  inset?: boolean
}

export function GlassCard({
  className,
  tone = 'default',
  interactive = false,
  inset = false,
  children,
  ...props
}: GlassCardProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl',
        'bg-white/80 dark:bg-neutral-950/40 backdrop-blur-xl',
        'ring-1 ring-inset',
        TONE_RING[tone],
        'shadow-[0_1px_0_0_rgba(255,255,255,0.4)_inset] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset]',
        interactive && 'transition-transform duration-300 motion-reduce:transition-none hover:-translate-y-0.5 hover:ring-neutral-300 dark:hover:ring-white/20',
        !inset && 'p-4 md:p-5',
        className
      )}
      {...props}
    >
      <div className="relative">{children}</div>
    </div>
  )
}

export function GlassCardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-3 flex items-start justify-between gap-3', className)} {...props} />
}

export function GlassCardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-sm font-medium text-neutral-900 dark:text-neutral-900 dark:text-white/90 tracking-tight', className)} {...props} />
}

export function GlassCardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-xs text-neutral-500 dark:text-neutral-500 dark:text-white/50', className)} {...props} />
}
